import React, { useState, useEffect, useRef, memo, useCallback, useMemo } from 'react';
import './Login.css';
import link from '../../assets/images/link.svg';
import PrivacyPolicy from '../PrivacyPolicy/PrivacyPolicy';
import TermsAndConditions from '../TermsAndConditions/TermsAndConditions';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';

const API_URL = import.meta.env.VITE_API_URL;

const Login = memo(
  ({ isLoginOpen, setIsLoginOpen }) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const cardRef = useRef(null);
  const [showTermsAndConditions, setShowTermsAndConditions] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);

  // Use body scroll lock hook for better UX
  useBodyScrollLock(isLoginOpen);

  // Email validation regex - memoized for performance
  const emailRegex = useMemo(() => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, []);

  const validateEmail = (email) => {
    if (!email.trim()) {
      setEmailError('Email is required');
      return false;
    }
    if (!emailRegex.test(email.trim())) {
      setEmailError("Please enter a valid email address");
      return false;
    }
    setEmailError('');
    return true;
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    if (emailError) {
      validateEmail(value); // Clear error if email becomes valid
    }
  };

  const handleKeyDown = (e) => {
    // Prevent space key from being entered
    if (e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
    }
  };

  const handleSubmit = async () => {
    if (!validateEmail(email)) return;

    setIsLoading(true);
    setEmailError(''); // Clear any previous errors

    try {
      const response = await fetch(
        `${API_URL}/auth/redirect-by-email`,
        {
          method: 'POST',
          mode: 'cors',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ email }),
        }
      );

      if (!response.ok) {
        const errorMessages = {
          404: response.message || 'Email ID does not exist. Please contact admin.',
          429: response.message || 'Too many requests. Please try again later.',
        };

        const errorMessage =
          errorMessages[response.status] ||
          `Login failed. Please try again. (${response.status})`;

        throw new Error(errorMessage);
      }

      const data = await response.json();

      // Validate response structure
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid response format from server');
      }

      if (data.redirectUrl && typeof data.redirectUrl === 'string') {
        // Validate URL format before redirecting
        try {
          new URL(data.redirectUrl);
          window.location.href = data.redirectUrl;
        } catch (urlError) {
          throw new Error('Invalid redirect URL received from server');
        }
      } else {
        throw new Error('No valid redirect URL provided in response');
      }
    } catch (error) {
      setEmailError(
        error.message || 'An unexpected error occurred. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !isLoading) {
      handleSubmit();
    }
  };

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        cardRef.current &&
        !cardRef.current.contains(event.target) &&
        !showPrivacyPolicy &&
        !showTermsAndConditions
      ) {
        setIsLoginOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPrivacyPolicy, showTermsAndConditions, isLoginOpen]);

  if (!isLoginOpen) return null;

  return (
    <>
      <div className="login-container">
        {/* Background decoration */}
        <div className="background-decoration">
          <div className="bg-circle-1"></div>
          <div className="bg-circle-2"></div>
        </div>

        {/* Login Card */}
        <div className="login-card" ref={cardRef}>
          <div className="login-header">
            <h1 className="login-title">Log in</h1>
            <p className="login-subtitle">
              Please Login to access your account
            </p>
          </div>

          <div className="login-form">
            <div className="input-group">
              <input
                type="email"
                value={email}
                onChange={handleEmailChange}
                onKeyDown={handleKeyDown}
                onKeyPress={handleKeyPress}
                placeholder="Email"
                className={`email-input ${emailError ? 'error' : ''}`}
                disabled={isLoading}
              />
              {emailError && (
                <span className="error-message">{emailError}</span>
              )}
            </div>

            <button
              onClick={handleSubmit}
              disabled={isLoading || !email.trim() || !emailRegex.test(email.trim())}
              className="continue-btn"
            >
              {isLoading ? (
                <div className="loading-spinner">
                  <div className="spinner"></div>
                  Loading...
                </div>
              ) : (
                'Continue'
              )}
            </button>
          </div>

          <div className="footer-links">
            <a
              href="#"
              className="footer-link"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowTermsAndConditions(true);
              }}
            >
              Terms and Conditions
              <img src={link} alt="link" className="link-icon" />
            </a>

            <a
              href="#"
              className="footer-link"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowPrivacyPolicy(true);
              }}
            >
              Privacy and Security
              <img src={link} alt="link" className="link-icon" />
            </a>
          </div>
        </div>
      </div>
      {showTermsAndConditions && (
        <TermsAndConditions
          setShowTermsAndConditions={setShowTermsAndConditions}
          setIsLoginOpen={setIsLoginOpen}
        />
      )}
      {showPrivacyPolicy && (
        <PrivacyPolicy
          setShowPrivacyPolicy={setShowPrivacyPolicy}
          setIsLoginOpen={setIsLoginOpen}
        />
      )}
          </>
    );
  });

export default Login;
